// Libraries
import { useCallback, useEffect, useId, useRef } from 'react';
// Types
import { Common } from '@shared/common/types/main';
import { KEYBOARD_KEY } from '@shared/common/constants/KeyboardKey';
import { ReactComponent as CloseSvg } from '@assets/icons/close.svg';
import Title, { TitleTag, TitleVariant } from '../typography/Title';
import Button, { ButtonVariant } from '../Button/Button';
import styles from './Modal.module.scss';
import classNames from 'classnames';
import ContentWraper from '../ContentWrapper/ContentWraper';

export enum ModalSize {
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
}

interface ModalProps extends Common {
  children: React.ReactNode;
  isOpen?: boolean;
  closeHandler: () => void;
  onSave?: () => void;
  wrapperClasses?: string;
  classes?: string;
  title?: string | JSX.Element;
  maxSize?: ModalSize;
  saveText?: string;
  titleClasses?: string;
  hideCloseBtn?: boolean;
  closeLabel?: string;
}

const focusableSelector = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = (wrapper: HTMLElement | null) => {
  if (!wrapper) return [];
  return Array.from(wrapper.querySelectorAll<HTMLElement>(focusableSelector)).filter((element) => {
    const bounds = element.getBoundingClientRect();
    return bounds.width > 0 && bounds.height > 0;
  });
};

const Modal = (props: ModalProps) => {
  const {
    children,
    closeHandler,
    wrapperClasses,
    classes,
    isOpen,
    title,
    maxSize,
    onSave,
    saveText,
    titleClasses,
    hideCloseBtn,
    closeLabel = 'Close modal',
  } = props;
  const titleId = `modal-title-${useId().replace(/:/g, '')}`;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const closeHandlerRef = useRef(closeHandler);
  const isActive = isOpen !== false;
  closeHandlerRef.current = closeHandler;

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === KEYBOARD_KEY.Escape) {
      event.preventDefault();
      closeHandlerRef.current();
      return;
    }
    if (event.key !== KEYBOARD_KEY.Tab) return;

    const wrapper = wrapperRef.current;
    const focusableElements = getFocusableElements(wrapper);
    if (!wrapper || focusableElements.length === 0) {
      event.preventDefault();
      wrapper?.focus();
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement;
    if (event.shiftKey && (activeElement === firstElement || !wrapper.contains(activeElement))) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && (activeElement === lastElement || !wrapper.contains(activeElement))) {
      event.preventDefault();
      firstElement.focus();
    }
  }, []);

  useEffect(() => {
    if (!isActive) return undefined;

    const previouslyFocused = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const wrapper = wrapperRef.current;
    const firstFocusableElement = getFocusableElements(wrapper)[0];
    (firstFocusableElement ?? wrapper)?.focus();
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused?.isConnected) previouslyFocused.focus();
    };
  }, [handleKeyDown, isActive]);

  return (
    <div
      ref={wrapperRef}
      role="dialog"
      aria-modal={isActive ? true : undefined}
      aria-hidden={isActive ? undefined : true}
      aria-labelledby={title ? titleId : undefined}
      className={classNames('modal', classes)}
      data-is-open={isOpen}
      data-max-size={maxSize}
      tabIndex={-1}
    >
      <ContentWraper classes={wrapperClasses}>
        {!!!hideCloseBtn && (
          <button aria-label={closeLabel} className="modal__button" onClick={closeHandler} type="button">
            <CloseSvg />
          </button>
        )}
        {!!title && (
          <Title
            id={titleId}
            classes={classNames('modal__title', titleClasses)}
            tag={TitleTag.h3}
            variant={TitleVariant.section}
          >
            {title}
          </Title>
        )}
        {children}
        {onSave && saveText && (
          <div className={styles.modalButtons}>
            <Button classes={styles.modalButton} type="button" variant={ButtonVariant.Outline} onClick={closeHandler}>
              Cancel
            </Button>
            <Button classes={styles.modalButton} type="button" onClick={onSave}>
              {saveText}
            </Button>
          </div>
        )}
      </ContentWraper>
    </div>
  );
};

export default Modal;
